
import React from 'react';
import { SimulationConfig } from './types';
import { SectionLabel } from './SectionLabel';
import { SmartInput } from './SmartInput';
import { Toggle } from './Toggle';
import { ControlGroup } from './ControlGroup';

interface EscapeAdvancedSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const EscapeAdvancedSection: React.FC<EscapeAdvancedSectionProps> = ({ config, setConfig }) => {
  const isEscape = config.mode === 'ESCAPE' || config.mode === 'RHYTHMIC_ESCAPE';
  const escapeMsg = "This setting is designed specifically for Escape/Hybrid modes.";

  return (
    <div className="w-full flex flex-col gap-3 p-4 rounded-3xl border bg-blue-500/5 border-blue-500/20 mb-4">
      <SectionLabel icon="fa-cog" darkMode={config.darkMode}>Escape Advanced</SectionLabel>
      
      <SmartInput 
          label="Inner Start Radius" 
          min={10} max={500} 
          value={config.containerRadius} 
          onChange={v => setConfig(p => ({...p, containerRadius: v}))} 
          darkMode={config.darkMode} 
          tooltip="The initial radius of the innermost ring."
          disabled={!isEscape}
          disabledMessage={escapeMsg}
          modes={['ESCAPE']}
      />

      <div className="flex flex-col gap-3">
          <ControlGroup label="Dynamic Shrink" tooltip="If enabled, rings shrink over time even if they aren't hit." disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']}>
            <div className="flex items-center justify-between border border-blue-500/10 p-2 rounded-xl">
              <span className="text-[8px] font-black opacity-60 uppercase text-center">Dynamic Shrink</span>
              <Toggle active={config.escapeDynamicShrink} onClick={() => setConfig(p => ({...p, escapeDynamicShrink: !p.escapeDynamicShrink}))} />
            </div>
          </ControlGroup>
          <ControlGroup label="Shrink Mode" tooltip="Changes how the rings shrink (e.g., linear vs exponential)." disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']}>
            <div className="flex items-center justify-between border border-blue-500/10 p-2 rounded-xl">
              <span className="text-[8px] font-black opacity-60 uppercase text-center">Shrink Mode</span>
              <Toggle active={config.escapeShrinkMode} onClick={() => setConfig(p => ({...p, escapeShrinkMode: !p.escapeShrinkMode}))} />
            </div>
          </ControlGroup>
          <ControlGroup label="Outer Borders" tooltip="Show the outermost boundary of the escape arena." disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']}>
            <div className="flex items-center justify-between border border-blue-500/10 p-2 rounded-xl">
              <span className="text-[8px] font-black opacity-60 uppercase text-center">Outer Borders</span>
              <Toggle active={config.escapeShowOuterBorders} onClick={() => setConfig(p => ({...p, escapeShowOuterBorders: !p.escapeShowOuterBorders}))} />
            </div>
          </ControlGroup>
          <ControlGroup label="Stop Rotation on Hit" tooltip="Briefly pauses the rotation of a ring when it is struck by the ball." disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']}>
            <div className="flex items-center justify-between border border-blue-500/10 p-2 rounded-xl">
              <span className="text-[8px] font-black opacity-60 uppercase text-center">Stop Rotation on Hit</span>
              <Toggle active={config.escapeStopRotationOnHit} onClick={() => setConfig(p => ({...p, escapeStopRotationOnHit: !p.escapeStopRotationOnHit}))} />
            </div>
          </ControlGroup>
      </div>
      <SmartInput label="Min Radius" min={0} max={100} value={config.escapeMinRadius} onChange={v => setConfig(p => ({...p, escapeMinRadius: v}))} darkMode={config.darkMode} tooltip="The smallest radius a ring can reach before it is considered 'broken' or stops shrinking." disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']} />
      <SmartInput label="Shrink Rate" min={0} max={10} step={0.1} value={config.escapeShrinkRate} onChange={v => setConfig(p => ({...p, escapeShrinkRate: v}))} darkMode={config.darkMode} tooltip="How fast the rings shrink over time." disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']} />
      <SmartInput label="Throw Angle (Deg)" min={0} max={360} value={config.escapeThrowAngle} onChange={v => setConfig(p => ({...p, escapeThrowAngle: v}))} darkMode={config.darkMode} tooltip="The initial angle at which the ball is launched." disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']} />
      <SmartInput label="Throw Power" min={1} max={100} value={config.escapeThrowPower} onChange={v => setConfig(p => ({...p, escapeThrowPower: v}))} darkMode={config.darkMode} tooltip="The initial velocity of the ball." disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']} />
      
      <div className="h-px bg-white/10 my-2" />
      <span className="text-[9px] font-black uppercase opacity-60">Shatter Physics (Gravity Dust)</span>
      <SmartInput label="Blast Force" min={0} max={1000} step={10} value={config.shatterForce ?? 100} onChange={v => setConfig(p => ({...p, shatterForce: v}))} darkMode={config.darkMode} tooltip="The power of the explosion when a ring shatters." disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']} />
      <SmartInput label="Gravity" min={0} max={2000} step={50} value={config.shatterGravity ?? 500} onChange={v => setConfig(p => ({...p, shatterGravity: v}))} darkMode={config.darkMode} tooltip="The downward force applied to ring fragments." disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']} />
  
      {/* PERFORMANCE OPTIMIZATION SECTION */}
      <div className="h-px bg-white/10 my-2" />
      <SectionLabel icon="fa-microchip" darkMode={config.darkMode}>Performance Tuner</SectionLabel>
      <div className={`flex flex-col gap-3 p-3 rounded-2xl bg-red-500/5 border border-red-500/20 transition-all duration-300 ${!isEscape ? 'opacity-30 grayscale pointer-events-none' : ''}`}>
          <p className="text-[8px] opacity-60 italic text-center">Adjust these if breakage causes lag.</p>
          <SmartInput 
              label="Dust Density" 
              min={0.1} max={1.0} step={0.1}
              value={config.dustResolution ?? 0.5} 
              onChange={v => setConfig(p => ({...p, dustResolution: v}))} 
              darkMode={config.darkMode} 
              tooltip="Controls the number of particles generated during a shatter effect."
              disabled={!isEscape}
              disabledMessage={escapeMsg}
              modes={['ESCAPE']}
          />
          <SmartInput 
              label="Max Fragments" 
              min={10} max={500} step={10}
              value={config.maxDustParticles ?? 100} 
              onChange={v => setConfig(p => ({...p, maxDustParticles: v}))} 
              darkMode={config.darkMode} 
              tooltip="The maximum number of active particles allowed on screen."
              disabled={!isEscape}
              disabledMessage={escapeMsg}
              modes={['ESCAPE']}
          />
          <SmartInput 
              label="Max Active Blasts" 
              min={50} max={500} step={10}
              value={config.maxActiveBlasts ?? 200} 
              onChange={v => setConfig(p => ({...p, maxActiveBlasts: v}))} 
              darkMode={config.darkMode} 
              tooltip="Limits the number of simultaneous explosion effects."
              disabled={!isEscape}
              disabledMessage={escapeMsg}
              modes={['ESCAPE']}
          />
      </div>
  
      <SectionLabel icon="fa-arrows-split-up-and-left" darkMode={config.darkMode}>Early Stage Overrides</SectionLabel>
      <div className={`flex flex-col gap-3 p-3 rounded-2xl bg-black/5 border border-white/5 transition-all duration-300 ${!isEscape ? 'opacity-30 grayscale pointer-events-none' : ''}`}>
          <ControlGroup label="Opposite Gap Rings" tooltip="Sets the first N rings to have their gaps rotated 180 degrees from the others." disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']}>
              <div className="flex justify-between items-center mb-1">
                  <span className="text-[8px] opacity-60">First N rings rotate 180°</span>
              </div>
              <input 
                  type="range" min={0} max={20} step={1} 
                  value={config.escapeOppositeGapCount || 0} 
                  onChange={e => setConfig(p => ({...p, escapeOppositeGapCount: parseInt(e.target.value)}))} 
                  className="w-full accent-blue-500"
              />
              <div className="text-right text-[9px] font-mono font-bold">{config.escapeOppositeGapCount || 0} rings</div>
          </ControlGroup>
          
          <SmartInput 
              label="Opposite Gap Size" 
              min={5} max={120} 
              value={config.escapeOppositeGapSize || 30} 
              onChange={v => setConfig(p => ({...p, escapeOppositeGapSize: v}))} 
              darkMode={config.darkMode} 
              tooltip="The width of the gaps for the 'Opposite Gap' rings."
              disabled={!isEscape}
              disabledMessage={escapeMsg}
              modes={['ESCAPE']}
          />
      </div>
  
      <div className="h-px bg-white/10 my-2" />
      <SmartInput label="Max Visible Rings" min={10} max={1000} value={config.escapeMaxVisibleRings} onChange={v => setConfig(p => ({...p, escapeMaxVisibleRings: v}))} darkMode={config.darkMode} tooltip="Limits how many rings are rendered at once for performance." disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']} />
      <SmartInput label="Ring Thickness" min={1} max={30} value={config.escapeRingThickness} onChange={v => setConfig(p => ({...p, escapeRingThickness: v}))} darkMode={config.darkMode} tooltip="The visual width of the rings." disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']} />
      <SmartInput label="Alpha Base" min={0} max={1} step={0.01} value={config.escapeRingAlphaBase} onChange={v => setConfig(p => ({...p, escapeRingAlphaBase: v}))} darkMode={config.darkMode} tooltip="The starting transparency of the rings." disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']} />
      <SmartInput label="Alpha Fade Curve" min={0} max={2} step={0.01} value={config.escapeRingAlphaFade} onChange={v => setConfig(p => ({...p, escapeRingAlphaFade: v}))} darkMode={config.darkMode} tooltip="How quickly the rings fade out as they get further from the center." disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']} />
      <SmartInput label="Blast Duration (ms)" min={500} max={10000} value={config.blastDuration} onChange={v => setConfig(p => ({...p, blastDuration: v}))} darkMode={config.darkMode} tooltip="How long the shatter effect remains visible." disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']} />
    </div>
  );
};
